import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  School, TrendingUp, Calendar, Headphones, Map, Cog, Plus, Search, X,
  Loader2, FileText, Video, StickyNote, Trash2, Edit3, Eye, Download,
  Users, Lock, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICONS = { TrendingUp, Calendar, Headphones, Map, Cog };
const COLORS = {
  sky: { chip: "bg-sky-100 text-sky-900 border-sky-200", ring: "ring-sky-300", hero: "from-sky-500/10 to-sky-100/60" },
  emerald: { chip: "bg-emerald-100 text-emerald-900 border-emerald-200", ring: "ring-emerald-300", hero: "from-emerald-500/10 to-emerald-100/60" },
  violet: { chip: "bg-violet-100 text-violet-900 border-violet-200", ring: "ring-violet-300", hero: "from-violet-500/10 to-violet-100/60" },
  amber: { chip: "bg-amber-100 text-amber-900 border-amber-200", ring: "ring-amber-300", hero: "from-amber-500/10 to-amber-100/60" },
  rose: { chip: "bg-rose-100 text-rose-900 border-rose-200", ring: "ring-rose-300", hero: "from-rose-500/10 to-rose-100/60" },
};

const KIND_META = {
  document: { icon: FileText, label: "Documento", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  loom: { icon: Video, label: "Vídeo Loom", cls: "bg-red-50 text-red-800 border-red-200" },
  note: { icon: StickyNote, label: "Nota", cls: "bg-zinc-50 text-zinc-800 border-zinc-200" },
};

// ----------------------------------------------------------------------------
// Loom embed: transforma la URL de compartir en URL de embed y muestra en iframe.
// ----------------------------------------------------------------------------
function LoomEmbed({ url }) {
  const embedUrl = useMemo(() => {
    try {
      // formato compartir: https://www.loom.com/share/<id>
      const m = url.match(/loom\.com\/share\/([\w-]+)/i);
      if (m) return `https://www.loom.com/embed/${m[1]}`;
      return url.replace("/share/", "/embed/");
    } catch { return url; }
  }, [url]);
  return (
    <div className="relative w-full overflow-hidden rounded-lg bg-black" style={{ paddingBottom: "56.25%" }}>
      <iframe
        src={embedUrl}
        title="Loom video"
        className="absolute inset-0 h-full w-full"
        allowFullScreen
        allow="fullscreen"
        data-testid="loom-iframe"
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Diálogo Añadir/Editar
// ----------------------------------------------------------------------------
function ItemFormDialog({ section, item, rolesCatalog, onClose, onSaved }) {
  const isEdit = !!item;
  const [kind, setKind] = useState(item?.kind || "document");
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.description || "");
  const [loomUrl, setLoomUrl] = useState(item?.loom_url || "");
  const [tags, setTags] = useState((item?.tags || []).join(", "));
  const [audience, setAudience] = useState(item?.audience || section.default_audience || []);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggleRole = (r) => setAudience((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);

  const validate = () => {
    if (!title.trim()) return "Falta el título";
    if (kind === "loom" && !loomUrl.includes("loom.com")) return "URL de Loom inválida";
    if (kind === "document" && !isEdit && !file) return "Adjunta un documento";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/formacion/items/${item.id}`, {
          title, description, loom_url: loomUrl || null,
          tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
          audience,
        });
      } else {
        const fd = new FormData();
        fd.append("section", section.key);
        fd.append("kind", kind);
        fd.append("title", title);
        fd.append("description", description || "");
        if (loomUrl) fd.append("loom_url", loomUrl);
        fd.append("tags", tags);
        fd.append("audience", audience.join(","));
        if (file) fd.append("file", file);
        await api.post("/formacion/items", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(isEdit ? "Contenido actualizado" : "Contenido añadido");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Error al guardar"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="formacion-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <Edit3 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
            {isEdit ? "Editar contenido" : `Añadir a ${section.label}`}
          </DialogTitle>
          <DialogDescription>{isEdit ? "Actualiza los datos del contenido." : "Sube un documento, incrusta un vídeo de Loom o crea una nota."}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!isEdit && (
            <div>
              <Label className="text-xs">Tipo de contenido</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {Object.entries(KIND_META).map(([k, m]) => {
                  const I = m.icon;
                  return (
                    <button key={k} type="button" onClick={() => setKind(k)}
                      className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-xs font-medium transition
                        ${kind === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted"}`}
                      data-testid={`formacion-kind-${k}`}>
                      <I className="h-4 w-4" />{m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs">Título *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Cómo cotizar un viaje a Japón" data-testid="formacion-title" />
          </div>
          <div>
            <Label className="text-xs">Descripción (opcional)</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Contexto, objetivo del contenido, para quién es útil…" data-testid="formacion-description" />
          </div>

          {kind === "document" && !isEdit && (
            <div>
              <Label className="text-xs">Archivo (PDF, Word, PPT, imagen) · máx 20 MB *</Label>
              <Input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg,.webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="formacion-file" />
              {file && <p className="mt-1 text-[11px] text-muted-foreground">✓ {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB</p>}
            </div>
          )}

          {(kind === "loom" || isEdit) && (
            <div>
              <Label className="text-xs">URL de Loom {kind === "loom" ? "*" : "(opcional)"}</Label>
              <Input value={loomUrl} onChange={(e) => setLoomUrl(e.target.value)} placeholder="https://www.loom.com/share/..." data-testid="formacion-loom-url" />
              <p className="mt-1 text-[11px] text-muted-foreground">Copia la URL de compartir desde Loom (formato share o embed).</p>
            </div>
          )}

          <div>
            <Label className="text-xs">Tags (separados por comas)</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="cotización, cierre, kimkim" data-testid="formacion-tags" />
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1.5">
              <Users className="h-3 w-3" />Audiencia (roles con acceso)
            </Label>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Si dejas vacío = todos. Dirección siempre ve todo.</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {rolesCatalog.map((r) => {
                const on = audience.includes(r.key);
                return (
                  <button key={r.key} type="button" onClick={() => toggleRole(r.key)}
                    className={`rounded-full border px-2.5 py-1 text-[10px] font-medium transition ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
                    data-testid={`formacion-role-${r.key}`}>
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="formacion-save">
            {saving ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Guardando…</> : (isEdit ? "Guardar cambios" : "Añadir")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------
// Card de item
// ----------------------------------------------------------------------------
function ItemCard({ item, section, canEdit, onEdit, onDelete, onView }) {
  const K = KIND_META[item.kind] || KIND_META.note;
  const KIcon = K.icon;
  const color = COLORS[section.color] || COLORS.sky;
  return (
    <Card className={`group overflow-hidden transition hover:shadow-lg hover:ring-1 ${color.ring}`} data-testid={`formacion-card-${item.id}`}>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start gap-2">
          <div className={`shrink-0 rounded-lg border p-2 ${color.chip}`}>
            <KIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="line-clamp-2 text-sm font-semibold" data-testid={`formacion-title-${item.id}`}>{item.title}</h4>
            <p className="mt-0.5 text-[10px] text-muted-foreground">{K.label} · {item.created_by_name || "—"}</p>
          </div>
          {item.audience?.length > 0 && (
            <Lock className="h-3.5 w-3.5 text-muted-foreground" title={`Restringido a: ${item.audience.join(", ")}`} />
          )}
        </div>
        {item.description && <p className="line-clamp-3 text-xs text-muted-foreground">{item.description}</p>}
        {item.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 4).map((t) => <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>)}
          </div>
        )}
        <div className="flex items-center justify-between border-t pt-2">
          <Button size="sm" variant="default" onClick={() => onView(item)} className="h-7 px-2.5 text-[11px]" data-testid={`formacion-open-${item.id}`}>
            <Eye className="mr-1 h-3 w-3" />Abrir
          </Button>
          {canEdit && (
            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition">
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(item)} title="Editar" data-testid={`formacion-edit-${item.id}`}>
                <Edit3 className="h-3 w-3" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50" onClick={() => onDelete(item)} title="Borrar" data-testid={`formacion-del-${item.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Modal para VER un item
// ----------------------------------------------------------------------------
function ItemViewer({ item, onClose }) {
  const baseURL = api.defaults.baseURL;
  const fileUrl = item.kind === "document" ? `${baseURL}/formacion/items/${item.id}/file` : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh]" data-testid="formacion-viewer">
        <DialogHeader>
          <DialogTitle>{item.title}</DialogTitle>
          {item.description && <DialogDescription className="text-xs">{item.description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          {item.kind === "loom" && item.loom_url && <LoomEmbed url={item.loom_url} />}
          {item.kind === "document" && fileUrl && (
            <div className="space-y-2">
              <div className="rounded-lg border bg-zinc-50 p-2">
                <iframe src={fileUrl} title={item.title} className="h-[62vh] w-full rounded" data-testid="formacion-doc-frame" />
              </div>
              <div className="flex justify-end">
                <a href={fileUrl} download={item.file_name} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" data-testid="formacion-doc-download"><Download className="mr-1.5 h-3.5 w-3.5" />Descargar {item.file_name}</Button>
                </a>
              </div>
            </div>
          )}
          {item.kind === "note" && item.description && (
            <div className="prose prose-sm max-w-none whitespace-pre-wrap rounded-lg border bg-white p-4">{item.description}</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------
// Página principal Formación
// ----------------------------------------------------------------------------
export default function FormacionPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const currentSection = params.get("view") || "onboarding_ventas";
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const sectionsQ = useQuery({
    queryKey: ["formacion-sections"],
    queryFn: async () => (await api.get("/formacion/sections")).data,
  });
  const itemsQ = useQuery({
    queryKey: ["formacion-items", currentSection, q],
    queryFn: async () => (await api.get("/formacion/items", { params: { section: currentSection, q: q || undefined } })).data,
    enabled: !!currentSection,
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/formacion/items/${id}`)).data,
    onSuccess: () => { toast.success("Contenido borrado"); refresh(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["formacion-sections"] });
    qc.invalidateQueries({ queryKey: ["formacion-items"] });
  };

  const sections = sectionsQ.data?.sections || [];
  const canEdit = sectionsQ.data?.can_edit || false;
  const rolesCatalog = sectionsQ.data?.roles_available || [];
  const active = sections.find((s) => s.key === currentSection) || sections[0];
  const items = itemsQ.data?.rows || [];

  if (sectionsQ.isLoading || !active) {
    return <div className="p-6 space-y-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-64 w-full" /></div>;
  }

  const activeIcon = ICONS[active.icon] || School;
  const ActiveIcon = activeIcon;
  const color = COLORS[active.color] || COLORS.sky;

  return (
    <div className="p-6 space-y-6" data-testid="formacion-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <School className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Formación</h1>
          <Badge variant="outline" className="text-[10px]"><Sparkles className="mr-1 h-2.5 w-2.5" />Onboarding + Destinos + Ops</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Centro de aprendizaje del equipo. Documentos, vídeos Loom y notas por área.
          {canEdit && " Puedes añadir y editar contenido."}
        </p>
      </div>

      {/* Sección tabs */}
      <div className="flex flex-wrap gap-2" data-testid="formacion-tabs">
        {sections.map((s) => {
          const Icon = ICONS[s.icon] || School;
          const on = s.key === currentSection;
          const c = COLORS[s.color] || COLORS.sky;
          return (
            <button key={s.key} onClick={() => { setParams({ view: s.key }); setQ(""); }}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${on ? `${c.chip} border-2 ${c.ring} ring-2` : "border-border bg-card hover:bg-muted"}`}
              data-testid={`formacion-tab-${s.key}`}>
              <Icon className="h-4 w-4" />
              {s.label}
              <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">{s.count}</Badge>
            </button>
          );
        })}
      </div>

      {/* Hero de la sección */}
      <Card className={`border-0 bg-gradient-to-br ${color.hero}`}>
        <CardContent className="flex items-start gap-4 p-5">
          <div className={`rounded-xl border p-3 ${color.chip}`}>
            <ActiveIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold">{active.label}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{active.description}</p>
          </div>
          {canEdit && (
            <Button onClick={() => { setEditing(null); setOpenForm(true); }} data-testid="formacion-add-btn">
              <Plus className="mr-1.5 h-4 w-4" />Añadir contenido
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Búsqueda */}
      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en esta sección…" className="pl-8" data-testid="formacion-search" />
        {q && <button onClick={() => setQ("")} className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
      </div>

      {/* Grid de items */}
      {itemsQ.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="formacion-empty">
          <School className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">Aún no hay contenido en &ldquo;{active.label}&rdquo;</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {canEdit ? "Pulsa \"Añadir contenido\" para subir el primer documento o vídeo." : "Vuelve pronto — se irá enriqueciendo con material del equipo."}
          </p>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="formacion-grid">
          {items.map((it) => (
            <ItemCard key={it.id} item={it} section={active} canEdit={canEdit}
              onEdit={(x) => { setEditing(x); setOpenForm(true); }}
              onDelete={(x) => { if (confirm(`¿Borrar "${x.title}"?`)) delMut.mutate(x.id); }}
              onView={setViewing} />
          ))}
        </div>
      )}

      {openForm && (
        <ItemFormDialog section={active} item={editing} rolesCatalog={rolesCatalog}
          onClose={() => { setOpenForm(false); setEditing(null); }}
          onSaved={() => { setOpenForm(false); setEditing(null); refresh(); }} />
      )}
      {viewing && <ItemViewer item={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
